import {AbstractControl} from '@angular/forms';
import {Observable, debounceTime, distinctUntilChanged, switchMap, of, catchError} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

interface ApiResponse<T> {
  success: boolean;
  response?: T;
  error?: HttpErrorResponse;
}

export class FormDebounceOperator {
  static create<T>({
    control,
    apiCall,
    options: {debounceMs = 300, emptyValue, beforeRequest, minLength = 0} = {},
  }: {
    control: AbstractControl | null;
    apiCall: (value: string) => Observable<T>;
    options?: {
      debounceMs?: number;
      emptyValue?: T;
      beforeRequest?: () => void;
      minLength?: number;
    };
  }): Observable<ApiResponse<T>> {
    if (!control) {
      return of({
        success: false,
        error: new HttpErrorResponse({error: 'No form control provided'}),
      });
    }

    return control.valueChanges.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      switchMap((value: unknown): Observable<ApiResponse<T>> => {
        const stringValue = String(value ?? '').trim();

        if (stringValue.length < minLength) {
          return of({
            success: true,
            response: emptyValue,
          });
        }

        beforeRequest?.();

        return apiCall(stringValue).pipe(
          switchMap((response) =>
            of({
              success: true,
              response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of({
              success: false,
              error,
            }),
          ),
        );
      }),
    );
  }
}
